import type { Product } from "@/lib/product-utils";

function isPresentPdpValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function mergeSparseObjects<T extends Record<string, unknown>>(
  primary: T | null | undefined,
  fallback: T | null | undefined
): T | undefined {
  if (!primary && !fallback) return undefined;
  if (!primary) return fallback ? ({ ...fallback } as T) : undefined;
  if (!fallback) return primary;

  const merged: Record<string, unknown> = { ...fallback };
  for (const [key, value] of Object.entries(primary)) {
    if (isPresentPdpValue(value)) {
      merged[key] = value;
    }
  }

  return merged as T;
}

export function mergeMissingPdpFields(serverProduct: Product, cachedProduct: Product): Product {
  const merged: Product = { ...serverProduct };

  if ((!merged.description || merged.description.length === 0) && cachedProduct.description) {
    merged.description = cachedProduct.description;
  }

  if (
    (!Array.isArray(merged.materials) || merged.materials.length === 0) &&
    Array.isArray(cachedProduct.materials) &&
    cachedProduct.materials.length > 0
  ) {
    merged.materials = cachedProduct.materials;
  }

  if (
    (!merged.color_info?.colors || merged.color_info.colors.length === 0) &&
    cachedProduct.color_info?.colors &&
    cachedProduct.color_info.colors.length > 0
  ) {
    merged.color_info = cachedProduct.color_info;
  }

  if (
    (!Array.isArray(merged.sizes) || merged.sizes.length === 0) &&
    Array.isArray(cachedProduct.sizes) &&
    cachedProduct.sizes.length > 0
  ) {
    merged.sizes = cachedProduct.sizes;
  }

  if (!merged.fit && cachedProduct.fit) {
    merged.fit = cachedProduct.fit;
  }

  if (!merged.dimensions && cachedProduct.dimensions) {
    merged.dimensions = cachedProduct.dimensions;
  }

  if (
    (!Array.isArray(merged.patterns) || merged.patterns.length === 0) &&
    Array.isArray(cachedProduct.patterns) &&
    cachedProduct.patterns.length > 0
  ) {
    merged.patterns = cachedProduct.patterns;
  }

  if (
    (!Array.isArray(merged.categories) || merged.categories.length === 0) &&
    Array.isArray(cachedProduct.categories) &&
    cachedProduct.categories.length > 0
  ) {
    merged.categories = cachedProduct.categories;
  }

  if (!merged.audience && cachedProduct.audience) {
    merged.audience = cachedProduct.audience;
  }

  if (!merged.gtin && cachedProduct.gtin) {
    merged.gtin = cachedProduct.gtin;
  }

  merged.offers = mergeSparseObjects(
    merged.offers as Record<string, unknown> | null | undefined,
    cachedProduct.offers as Record<string, unknown> | null | undefined
  ) as Product["offers"];

  merged.enrichment = mergeSparseObjects(
    merged.enrichment as Record<string, unknown> | null | undefined,
    cachedProduct.enrichment as Record<string, unknown> | null | undefined
  ) as Product["enrichment"];

  return merged;
}
